import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  CircularProgress
} from "@mui/material";
import { useEffect } from "react";
import { useState } from "react";
import { BoxColumn } from "../../components/common/Boxes";
import { shortenAddress } from "../../components/common/util";
import { useAuth } from "../../context/Authentication";
import {
  useJoinQuickmatchQueueMutation,
  useLeaveQuickmatchQueueMutation,
  useLobbiesQuery
} from "../../generated/graphql_schema";
import { LinkButton } from "../../components/common/LinkButton";
import { Modal } from "../../components/common/Modal";
import styles from "../../styles/Lobby.module.css";
import { useRouter } from "next/router";
import Image from "next/image";

export interface ILobbySettings {
  name: string;
  potencyCap?: number | null;
  requiredWins?: number;
}

export enum Board {
  ThreeByThree = "ThreeByThree",
  FourByFour = "FourByFour"
}

const LobbyPage = () => {
  const { data: lobbiesData, loading, error, refetch } = useLobbiesQuery();
  const [lobbySettingsModal, setlobbySettingsModal] =
    useState<ILobbySettings>();
  const [joinQuickmatchQueueMutation] = useJoinQuickmatchQueueMutation();
  const [leaveQuickmatchQueueMutation] = useLeaveQuickmatchQueueMutation();
  const [isInQuickmatchQueue, setIsInQuickmatchQueue] = useState(false);
  const router = useRouter();
  const { user } = useAuth();

  const handleClickQuickmatch = () => {
    setIsInQuickmatchQueue(true);
  };

  const handleCloseModal = () => {
    setlobbySettingsModal(undefined);
    setIsInQuickmatchQueue(false);
    leaveQuickmatchQueueMutation();
  };

  useEffect(() => {
    const interval = setInterval(() => {
      if (isInQuickmatchQueue) {
        joinQuickmatchQueueMutation().then((result) => {
          const activeLobbyName = result.data?.joinQuickmatchQueue?.name;
          if (activeLobbyName !== undefined) {
            const audio = new Audio("/notification-sound-effect.mp3");
            audio.play();
            router.push(`/lobby/${activeLobbyName}`);
          }
        });
      }
    }, 3000);
    return () => {
      clearInterval(interval);
      if (user) {
        leaveQuickmatchQueueMutation();
      }
    };
  }, [isInQuickmatchQueue, joinQuickmatchQueueMutation, user]);

  const ModalContent = isInQuickmatchQueue ? (
    <BoxColumn
      sx={{
        alignItems: "center",
        backgroundColor: "var(--modal-bg-color)",
        p: 3
      }}
    >
      <Typography variant="h3">Quickmatch</Typography>
      <CircularProgress />
      <Typography variant="h4">Waiting for opponent</Typography>
    </BoxColumn>
  ) : (
    <BoxColumn sx={{ p: 2, alignItems: "center" }}>
      <Typography variant="h3" sx={{ paddingBottom: 3 }}>
        Lobby Settings
      </Typography>
      <Typography variant="h5">
        Lobby Name: {lobbySettingsModal?.name}
      </Typography>
      <Typography variant="h5">
        Potency Cap:{" "}
        {lobbySettingsModal?.potencyCap ? lobbySettingsModal?.potencyCap : "∞"}
      </Typography>
      <Typography variant="h5" sx={{ paddingBottom: 3 }}>
        Required Wins: {lobbySettingsModal?.requiredWins}
      </Typography>

      <LinkButton
        sx={{ width: "fit-content" }}
        href={`/lobby/${lobbySettingsModal?.name}`}
        disabled={!user}
      >
        Join Lobby
      </LinkButton>
    </BoxColumn>
  );

  useEffect(() => {
    // todo is there better way? maybe subscribe to lobby cud
    refetch();
  }, [refetch]);

  // sort by active users
  const lobbiesSorted = [...(lobbiesData?.lobbies || [])].sort((a, b) => {
    if (a.activeUsers > b.activeUsers) return -1;
    if (b.activeUsers > a.activeUsers) return 1;
    return 0;
  });

  //@ts-ignore
  const displayName = (lobby) => {
    if (
      lobby.user.__typename == "User" &&
      lobby.user.ensName !== undefined &&
      lobby.user.ensName !== null
    ) {
      return lobby.user.ensName;
    } else {
      return shortenAddress(lobby.user.publicAddress);
    }
  };

  return (
    <BoxColumn
      sx={{ p: 2, alignItems: "center", overflowY: "auto", height: "100%" }}
    >
      <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
        <Typography variant="h2">Lobbies</Typography>
      </Box>
      <TableContainer
        sx={{
          width: 600,
          maxWidth: "100%",
          flex: "0 0 75%",
          maxHeight: "75vh",
          mb: 2
        }}
        className={styles.LargeFontParent}
      >
        <Table
          stickyHeader
          sx={{
            background: "rgb(0,0,0,1)",
            borderRadius: 1
          }}
        >
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Host</TableCell>
              <TableCell>Active Users</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && (
              <TableRow>
                <TableCell>
                  <CircularProgress />
                </TableCell>
              </TableRow>
            )}
            {!loading &&
              (!lobbiesSorted ||
                (!lobbiesSorted.length && (
                  <TableRow>
                    {" "}
                    <TableCell colSpan={6}>no lobbies found</TableCell>{" "}
                  </TableRow>
                )))}

            {lobbiesSorted?.map((lobby) => (
              <TableRow
                key={lobby.id}
                hover
                onClick={() => {
                  if (user) {
                    setlobbySettingsModal({
                      name: lobby.name,
                      potencyCap: lobby.settings?.potencyCap,
                      requiredWins: lobby.settings?.numberOfWins
                    });
                  }
                }}
                sx={{ cursor: "pointer" }}
              >
                <TableCell>{lobby.name}</TableCell>
                <TableCell>{displayName(lobby)}</TableCell>
                <TableCell>{lobby.activeUsers.length}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {user ? (
        <Box sx={{ display: "flex" }}>
          <Button onClick={handleClickQuickmatch}>Quickmatch</Button>
          <LinkButton sx={{ ml: 2 }} href="/lobby/create">
            Create Lobby
          </LinkButton>
          <LinkButton sx={{ ml: 2 }} href="/vs-ai">
            Play vs AI
          </LinkButton>
        </Box>
      ) : (
        <Typography variant="h5" sx={{ mt: 2 }}>
          Sign in to play
        </Typography>
      )}
      <Modal
        hideBackdrop={!isInQuickmatchQueue}
        open={Boolean(lobbySettingsModal) || isInQuickmatchQueue}
        onClose={handleCloseModal}
        style={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "fit-content",
          height: isInQuickmatchQueue ? "100%" : "fit-content",
          width: isInQuickmatchQueue ? "100%" : undefined,
          maxHeight: isInQuickmatchQueue ? "100%" : "fit-content",
          maxWidth: isInQuickmatchQueue ? "100%" : undefined,
          backgroundColor: isInQuickmatchQueue ? "rgb(0,0,0,0.5)" : undefined
        }}
      >
        {ModalContent}
      </Modal>
    </BoxColumn>
  );
};

// import dynamic from 'next/dynamic';

export default LobbyPage;

// const _LobbyPage = dynamic(() => import('./index.tsx'), {
//   ssr: false,
// });
